import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import Header from 'components/ui/Header';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const getUrgencyLabel = (urgency) => {
  const labels = { low: 'Bassa', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
  return labels?.[urgency] || urgency || '—';
};

const getUrgencyBadgeClass = (urgency) => {
  const classes = {
    low: 'bg-green-100 text-green-800 border border-green-200',
    medium: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    high: 'bg-orange-100 text-orange-800 border border-orange-200',
    urgent: 'bg-red-100 text-red-800 border border-red-200 animate-pulse',
  };
  return classes?.[urgency] || 'bg-gray-100 text-gray-700 border border-gray-200';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date?.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
};

const isValidUUID = (uuid) => {
  if (!uuid || typeof uuid !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(uuid);
};

const ProfessionalRequestDetailPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [imageSignedUrls, setImageSignedUrls] = useState([]);
  const [installmentsEnabled, setInstallmentsEnabled] = useState(false);

  useEffect(() => {
    const fetchRequest = async () => {
      if (authLoading) return;

      if (!user) return;

      if (!isValidUUID(requestId)) {
        setError('ID richiesta non valido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [requestResult, configResult] = await Promise.all([
          supabase?.from('service_requests')?.select('title, description, service_type, urgency, location_name, created_at, media_files, preferred_date, preferred_time_slot, additional_details, installments_requested')?.eq('id', requestId)?.single(),
          supabase?.from('platform_config')?.select('value')?.eq('key', 'installments_enabled')?.single(),
        ]);

        const data = requestResult?.data;
        const fetchError = requestResult?.error;
        const configData = configResult?.data;

        if (fetchError) {
          setError('Richiesta non trovata');
        } else {
          setRequest(data);
        }

        if (configData?.value === true) {
          setInstallmentsEnabled(true);
        }
      } catch (err) {
        console.error('Error fetching request:', err);
        setError('Si è verificato un errore durante il caricamento');
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [requestId, user, authLoading, navigate]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login-form');
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const loadSignedUrls = async () => {
      if (!request?.media_files?.length) return;
      const urls = await Promise.all(
        request?.media_files?.map(async (file) => {
          const { data } = await supabase?.storage?.from('service-requests')?.createSignedUrl(file?.storage_path, 3600);
          return data?.signedUrl || null;
        })
      );
      setImageSignedUrls(urls?.filter(Boolean));
    };
    loadSignedUrls();
  }, [request]);

  const imageUrls = Array.isArray(request?.media_files) ? request?.media_files : [];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-500 text-sm">Caricamento richiesta...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Errore</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button variant="default" onClick={() => navigate('/professional-dashboard')}>
            Torna alle opportunità
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/professional-dashboard')}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors mb-6"
        >
          <Icon name="ArrowLeft" size={16} />
          <span>Torna alle opportunità</span>
        </button>

        {/* Title + Urgency Badge */}
        <div className="flex flex-wrap items-start gap-3 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex-1 min-w-0">
            {request?.title || '—'}
          </h1>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium flex-shrink-0 ${getUrgencyBadgeClass(request?.urgency)}`}
          >
            {getUrgencyLabel(request?.urgency)}
          </span>
        </div>

        {/* Description Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
          <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Icon name="FileText" size={18} className="text-primary" />
            Descrizione
          </h2>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {request?.description || 'Nessuna descrizione disponibile.'}
          </p>
        </div>

        {/* Dettagli aggiuntivi */}
        {request?.additional_details && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Icon name="FileText" size={18} className="text-primary" />
              Dettagli aggiuntivi
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {request?.additional_details}
            </p>
          </div>
        )}

        {/* Foto e Immagini Section */}
        {imageUrls?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Icon name="Image" size={18} className="text-primary" />
              Foto e Immagini
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {imageUrls?.map((file, idx) => (
                <button
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <img
                    src={imageSignedUrls?.[idx] || '/assets/images/no_image.png'}
                    alt={file?.original_filename || `Immagine ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Info Cards Row 1: Tipo di Servizio + Urgenza */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Briefcase" size={16} className="text-primary" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tipo di Servizio</span>
            </div>
            <p className="text-gray-900 font-semibold text-base">
              {request?.service_type || '—'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Zap" size={16} className="text-primary" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Urgenza</span>
            </div>
            <span className={`inline-block px-2.5 py-1 rounded-full text-sm font-medium ${getUrgencyBadgeClass(request?.urgency)}`}>
              {getUrgencyLabel(request?.urgency)}
            </span>
          </div>
        </div>

        {/* Info Cards Row 2: Data Creazione + Località */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Calendar" size={16} className="text-primary" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Data Creazione</span>
            </div>
            <p className="text-gray-900 font-semibold text-base">
              {formatDate(request?.created_at)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="MapPin" size={16} className="text-primary" />
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Località</span>
            </div>
            <p className="text-gray-900 font-semibold text-base">
              {request?.location_name || '—'}
            </p>
          </div>
        </div>

        {/* Tempistiche preferite */}
        {(request?.preferred_date || request?.preferred_time_slot) && (() => {
          const timeSlotMap = {
            morning: 'Mattino (08:00 - 12:00)',
            afternoon: 'Pomeriggio (12:00 - 18:00)',
            evening: 'Sera (18:00 - 21:00)',
          };
          const formattedPreferredDate = request?.preferred_date
            ? (() => {
                const d = new Date(request.preferred_date);
                const day = String(d?.getUTCDate())?.padStart(2, '0');
                const month = String(d?.getUTCMonth() + 1)?.padStart(2, '0');
                const year = d?.getUTCFullYear();
                return `${day}/${month}/${year}`;
              })()
            : null;
          const timeSlotLabel = request?.preferred_time_slot
            ? (timeSlotMap?.[request?.preferred_time_slot] || request?.preferred_time_slot)
            : null;
          return (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Icon name="Clock" size={18} className="text-primary" />
                Tempistiche preferite
              </h2>
              <div className="flex flex-wrap gap-4">
                {formattedPreferredDate && (
                  <span className="text-gray-700 text-sm">
                    <span className="font-medium">Data preferita:</span> {formattedPreferredDate}
                  </span>
                )}
                {timeSlotLabel && (
                  <span className="text-gray-700 text-sm">
                    <span className="font-medium">Fascia oraria:</span> {timeSlotLabel}
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Installment Request Badge */}
        {installmentsEnabled && request?.installments_requested && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-1">
            <span className="text-blue-800 font-semibold text-sm">
              💳 Il cliente ha richiesto il pagamento rateale
            </span>
            <span className="text-blue-600 text-xs">
              Puoi caricare/ricaricare il preventivo con un'eventuale proposta di rateizzazione
            </span>
          </div>
        )}

        {/* Primary CTA Button */}
        <Button
          variant="default"
          size="lg"
          onClick={() => navigate(`/send-quote/${requestId}`)}
          iconName="Send"
          className="w-full"
        >
          Invia preventivo
        </Button>
      </div>
      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e?.stopPropagation()}>
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
            <img
              src={imageSignedUrls?.[lightboxIndex] || '/assets/images/no_image.png'}
              alt={imageUrls?.[lightboxIndex]?.original_filename || `Immagine ${lightboxIndex + 1}`}
              className="w-full rounded-lg max-h-[80vh] object-contain"
              onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
            />
            {imageUrls?.length > 1 && (
              <div className="flex justify-between mt-3">
                <button
                  onClick={() => setLightboxIndex((lightboxIndex - 1 + imageUrls?.length) % imageUrls?.length)}
                  className="text-white bg-white/20 hover:bg-white/30 rounded-lg px-4 py-2 text-sm transition-colors"
                >
                  ← Precedente
                </button>
                <span className="text-white text-sm self-center">
                  {lightboxIndex + 1} / {imageUrls?.length}
                </span>
                <button
                  onClick={() => setLightboxIndex((lightboxIndex + 1) % imageUrls?.length)}
                  className="text-white bg-white/20 hover:bg-white/30 rounded-lg px-4 py-2 text-sm transition-colors"
                >
                  Successiva →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalRequestDetailPage;
